import { useState, useEffect } from 'react';
import { Palette, Check, Clock, Star, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function HireDesigner() {
  const [promoActive, setPromoActive] = useState(false);
  const [hoursLeft, setHoursLeft] = useState(0);

  useEffect(() => {
    const promoShown = localStorage.getItem('designer_promo_shown');
    if (!promoShown) {
      setPromoActive(true);
      setHoursLeft(24);
    } else {
      const hoursSince = (Date.now() - parseInt(promoShown)) / 3600000;
      if (hoursSince < 24) {
        setPromoActive(true);
        setHoursLeft(Math.ceil(24 - hoursSince));
      }
    }
  }, []);

  const price = promoActive ? 375 : 750;

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="hire-designer-page">
      <div className="text-center">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center mx-auto mb-5">
          <Palette size={36} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white font-syne mb-2">Hire a Website Designer</h1>
        <p className="text-[#9CA3AF] text-base">Let a professional create your dream website while you focus on your business.</p>
      </div>

      {/* Pricing */}
      <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-8 text-center">
        {promoActive && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/15 text-green-400 text-sm font-bold mb-4">
            <Clock size={14} /> 50% OFF — {hoursLeft}h left!
          </div>
        )}
        <div className="flex items-center justify-center gap-3 mb-2">
          {promoActive && <span className="text-[#64748B] text-2xl line-through">$750</span>}
          <span className="text-5xl font-bold text-white font-syne">${price}</span>
        </div>
        <p className="text-[#9CA3AF] text-sm mb-6">One-time fee. Your website, your way.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left mb-8">
          {[
            'Custom design tailored to your brand',
            'Mobile-responsive layout',
            'SEO optimization included',
            'Content setup (up to 5 pages)',
            'Contact form & social links',
            'E-commerce setup if needed',
            '2 rounds of revisions',
            'Delivered within 7 business days',
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-[#C4B5FD]">
              <Check size={14} className="text-green-400 flex-shrink-0" /> {item}
            </div>
          ))}
        </div>

        <Button
          onClick={() => toast.success('Request submitted! A designer will reach out to you within 24 hours via Messages.')}
          className="w-full max-w-sm mx-auto bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold text-lg py-6 rounded-xl gap-2"
          data-testid="hire-designer-btn"
        >
          Hire a Designer — ${price} <ArrowRight size={18} />
        </Button>
      </div>

      {/* How it works */}
      <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white font-syne mb-4">How It Works</h2>
        <div className="space-y-4">
          {[
            { step: '1', title: 'Submit Your Request', desc: 'Click the button above and a designer will be assigned to you.' },
            { step: '2', title: 'Share Your Vision', desc: 'Chat with your designer in the Messages tab about your brand, colors, and goals.' },
            { step: '3', title: 'Review & Launch', desc: 'Get a preview of your site. Request changes (2 revisions included). Then go live!' },
          ].map((item) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center text-sm font-bold text-[#A78BFA] flex-shrink-0">{item.step}</div>
              <div>
                <p className="text-white font-medium text-sm">{item.title}</p>
                <p className="text-[#9CA3AF] text-xs">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
